import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Play, ArrowRight } from 'lucide-react';
import { useSound } from '../hooks/useSound';

const Hero = ({ data }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { playClickSound } = useSound();

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToSection = (id) => {
    playClickSound();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-950">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div
          className="absolute top-1/4 -left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse"
          style={{
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
            transition: 'transform 0.3s ease-out'
          }}
        />
        <div
          className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse"
          style={{
            transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px)`,
            transition: 'transform 0.3s ease-out'
          }}
        />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-32 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Profile Image and Text Combined */}
          <div className="flex flex-col md:flex-row items-center gap-12 mb-8">
            {/* Profile Image */}
            <div className="relative group flex-shrink-0 animate-float">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-yellow-500 rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500 animate-glow" />
              <div className="relative w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-emerald-500/30 shadow-2xl shadow-emerald-500/20 group-hover:scale-105 transition-transform duration-500 animate-breathe">
                <img
                  src={data.profileImage}
                  alt={data.name}
                  className="w-full h-full object-cover object-center"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=400&fit=crop';
                  }}
                />
              </div>
            </div>

            {/* Text Content */}
            <div className="text-center md:text-left space-y-6 flex-1">
              {/* Badge with Wave Animation */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-sm">
                <div className="relative flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <div className="absolute w-2 h-2 rounded-full bg-emerald-400 animate-wave opacity-75" />
                  <div className="absolute w-2 h-2 rounded-full bg-emerald-400 animate-wave-delayed opacity-50" />
                </div>
                <span className="text-emerald-400 text-sm font-medium">Online</span>
              </div>

              {/* Main Heading */}
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="text-white">HI, I'M {data.name}</span>
                <br />
                <span className="bg-gradient-to-r from-emerald-400 via-emerald-300 to-yellow-400 bg-clip-text text-transparent">
                  {data.subtitle}
                </span>
              </h1>

              {/* Subtitle Badge */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 text-slate-300">
                <span className="text-base md:text-lg font-medium">{data.tagline}</span>
                <span className="text-slate-500">·</span>
                <span className="text-base md:text-lg font-medium">{data.stats}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          <p className="text-lg text-slate-400 max-w-3xl mx-auto md:mx-0 leading-relaxed text-center md:text-left mb-8">
            {data.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4">
            <Button
              onClick={() => scrollToSection('portfolio')}
              size="lg"
              className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white border-0 shadow-xl shadow-emerald-500/30 px-8 group hover:scale-105 transition-all duration-300"
            >
              <Play className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              {data.cta}
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              variant="outline"
              className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500/50 px-8 group hover:scale-105 transition-all duration-300"
            >
              {data.secondaryCta}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          {/* Floating Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-12">
            {['4+ Years', '192 Reviews', '5.0 Rating'].map((stat, index) => (
              <div
                key={index}
                className={`group p-6 rounded-2xl bg-slate-900/50 backdrop-blur-sm border border-slate-800 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/10 hover:-translate-y-2 ${
                  index === 0 ? 'animate-float' : index === 1 ? 'animate-float-slow' : 'animate-float'
                }`}
                style={{
                  transform: `perspective(1000px) rotateY(${mousePosition.x * 0.02}deg) rotateX(${-mousePosition.y * 0.02}deg)`,
                  transition: 'transform 0.3s ease-out',
                  animationDelay: `${index * 0.2}s`
                }}
              >
                <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-yellow-500 bg-clip-text text-transparent">
                  {stat}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-emerald-500/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-emerald-400 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
